import {makeAutoObservable, runInAction} from 'mobx';
import createAxiosInstance from '../support/AxiosSupport';
import logger from '../utils/logger';

/**
 * Документ из реестра бэкенда (`GET /api/legal/pending`, `GET /api/legal/documents`):
 * совпадает с `LegalDocumentDto`.
 */
export interface PendingLegalDocument {
    type: string;
    title: string;
    version: string;
    effectiveOn: string;
    path: string;
}

interface AcceptResult {
    recorded: number;
    legalPending: boolean;
}

/** Код ошибки бэкенда: клиент показал уже не действующую редакцию (R-3). */
export const VERSION_MISMATCH = 'LEGAL_VERSION_MISMATCH';

export const STALE_VERSION_MESSAGE =
    'Версия документов изменилась. Список обновлён — прочитайте документы и подтвердите согласие ещё раз.';
export const ACCEPT_FAILED_MESSAGE = 'Не удалось сохранить согласие. Попробуйте ещё раз.';
export const LOAD_FAILED_MESSAGE = 'Не удалось загрузить список документов. Попробуйте ещё раз.';

/**
 * Повторное согласие с правовыми документами для уже зарегистрированных
 * пользователей (`docs/features/2026-08-26_legal-documents`, §9).
 *
 * Сигнал `pending` приходит из ответа входа/refresh (`AuthStatusDto.legalPending`) —
 * отдельный запрос на каждой странице не нужен. Список документов запрашивается
 * только когда диалог действительно открывается. «Позже» закрывает диалог до
 * следующего старта сессии: флаг живёт в памяти и обнуляется перезагрузкой.
 */
class LegalConsentStore {
    /** Есть непринятые документы — по сигналу бэкенда. */
    pending = false;
    /** Пользователь нажал «Позже» в этой сессии. */
    postponed = false;
    documents: PendingLegalDocument[] = [];
    loading = false;
    submitting = false;
    error: string | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    /** Показывать ли диалог. */
    get shouldPrompt(): boolean {
        return this.pending && !this.postponed;
    }

    setPending(value: boolean | undefined): void {
        this.pending = value === true;
        if (!this.pending) {
            this.documents = [];
        }
    }

    postpone(): void {
        this.postponed = true;
    }

    /** Сброс при выходе: следующий пользователь этой вкладки начинает с чистого состояния. */
    reset(): void {
        this.pending = false;
        this.postponed = false;
        this.documents = [];
        this.loading = false;
        this.submitting = false;
        this.error = null;
    }

    /** Актуальный список непринятых документов с версиями и путями. */
    async loadPending(): Promise<void> {
        this.loading = true;
        this.error = null;
        try {
            const api = await createAxiosInstance();
            const res = await api.get<PendingLegalDocument[]>('/legal/pending');
            const list = Array.isArray(res.data) ? res.data : [];
            runInAction(() => {
                this.documents = list;
                // Бэкенд мог зафиксировать согласие в другой вкладке: пустой список —
                // значит, показывать нечего.
                if (list.length === 0) {
                    this.pending = false;
                }
            });
        } catch (e) {
            logger.warn('LegalConsentStore: failed to load pending documents', {error: String(e)});
            runInAction(() => {
                this.error = LOAD_FAILED_MESSAGE;
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    /**
     * Фиксирует согласие со всеми загруженными документами. Версии могут
     * различаться между документами, а контракт принимает одну версию на запрос —
     * поэтому документы группируются по версии. Возвращает `true`, когда всё принято.
     */
    async accept(): Promise<boolean> {
        if (this.submitting || this.documents.length === 0) {
            return false;
        }
        this.submitting = true;
        this.error = null;
        try {
            const api = await createAxiosInstance();
            const byVersion = new Map<string, string[]>();
            this.documents.forEach((doc) => {
                byVersion.set(doc.version, [...(byVersion.get(doc.version) ?? []), doc.type]);
            });
            let stillPending = false;
            for (const [version, documentTypes] of Array.from(byVersion.entries())) {
                const res = await api.post<AcceptResult>('/legal/accept', {documentTypes, version});
                stillPending = res.data?.legalPending === true;
            }
            runInAction(() => {
                this.pending = stillPending;
                this.documents = [];
            });
            return !stillPending;
        } catch (e: unknown) {
            const response = (e as {response?: {status?: number; data?: {error?: string}}})?.response;
            const stale = response?.status === 409 && response?.data?.error === VERSION_MISMATCH;
            logger.warn('LegalConsentStore: accept failed', {status: response?.status, stale});
            runInAction(() => {
                this.error = stale ? STALE_VERSION_MESSAGE : ACCEPT_FAILED_MESSAGE;
            });
            if (stale) {
                // Реестр обновился, пока диалог был открыт: показываем действующие редакции.
                await this.loadPending();
                runInAction(() => {
                    this.error = STALE_VERSION_MESSAGE;
                });
            }
            return false;
        } finally {
            runInAction(() => {
                this.submitting = false;
            });
        }
    }
}

const legalConsentStore = new LegalConsentStore();
export default legalConsentStore;
export {LegalConsentStore};
